import {
  Button,
  Container,
  Link,
  Loading,
  Text,
  useModal,
} from "@fluidattacks/design";
import { Fragment, useCallback, useEffect, useRef } from "react";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { useTheme } from "styled-components";

import { CodeComponent } from "./code";
import {
  PERMISSION_REMEDIATION_LANGUAGES,
  UNSUPPORTED_LANGUAGES,
} from "./constants";
import { useCustomFix } from "./hooks/use-custom-fix";
import { useRemediationLanguages } from "./hooks/use-remediation-languages";
import { CustomFixModal } from "./modal";
import type { ICustomFixProps } from "./types";
import { sliceVulnerabilityId } from "./utils";

import { useAuthz } from "hooks/use-authz";
import { useFindingHeaderQuery } from "pages/finding/hooks";

const CustomH3 = (
  props: React.HTMLAttributes<HTMLHeadingElement>,
): JSX.Element => (
  <h3
    style={{
      color: "#9CA3AF",
      fontWeight: "normal",
      marginBottom: "0.5em",
      marginTop: "1.5em",
    }}
  >
    {props.children}
  </h3>
);

export const CustomFix: FC<ICustomFixProps> = ({
  vulnId,
  isAutoFixable,
  findingId,
  groupName,
}): JSX.Element => {
  const { t } = useTranslation();
  const modal = useModal("custom-fix-modal");
  const kbUrlPrefix = "https://help.fluidattacks.com/portal/en/kb/articles/";
  const theme = useTheme();
  const { canResolve } = useAuthz();
  const canRemediationLanguagesFixes = canResolve(
    PERMISSION_REMEDIATION_LANGUAGES,
  );
  const onClick = useCallback((): void => {
    modal.open();
  }, [modal]);

  const { customFixMarkdown, hasData, loading } = useCustomFix(
    vulnId,
    isAutoFixable,
  );

  const finding = useFindingHeaderQuery(findingId);
  const remediationId = sliceVulnerabilityId(finding?.finding.title ?? "");

  const { languages: rawLanguages, loading: loadingRemediationLanguages } =
    useRemediationLanguages({
      groupName,
      remediationId,
    });

  const languages = rawLanguages
    .filter(
      (language: string): boolean => !UNSUPPORTED_LANGUAGES.includes(language),
    )
    .map(
      (language: string): string =>
        language.charAt(0).toUpperCase() + language.slice(1).toLowerCase(),
    );
  const endRef = useRef<HTMLDivElement>(null);
  useEffect((): void => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [customFixMarkdown]);

  if (loading && isAutoFixable) {
    return (
      <Loading
        color={"red"}
        label={t("searchFindings.tabVuln.contentTab.customFix.loading")}
        size={"md"}
      ></Loading>
    );
  }

  const renderRemediationLanguages = (): ReactNode => {
    if (loadingRemediationLanguages) {
      return (
        <Loading
          color={"red"}
          label={t(
            "searchFindings.tabVuln.contentTab.customFix.criteria.loading",
          )}
          size={"md"}
        ></Loading>
      );
    }

    if (canRemediationLanguagesFixes) {
      return languages.map(
        (language): JSX.Element => (
          <Container
            display={"flex"}
            flexDirection={"row"}
            key={language}
            mb={0.25}
            ml={1}
          >
            <span style={{ marginRight: 8 }}>{"• "}</span>
            <Link
              href={`${kbUrlPrefix}criteria-fixes-${language}-${remediationId}`}
              iconPosition={"hidden"}
              target={"_blank"}
            >
              {language}
            </Link>
          </Container>
        ),
      );
    }

    return null;
  };

  return (
    <Fragment>
      <Container
        alignItems={"center"}
        display={"flex"}
        justify={"between"}
        mb={2}
      >
        <Text color={theme.palette.gray[800]} fontWeight={"bold"} size={"lg"}>
          {hasData
            ? t("searchFindings.tabVuln.contentTab.customFix.generateByIA")
            : t("searchFindings.tabVuln.contentTab.customFix.criteria.title")}
        </Text>
        <Button
          hidden={!isAutoFixable}
          icon={"wrench"}
          onClick={onClick}
          variant={"primary"}
        >
          {"Auto-fix"}
        </Button>
      </Container>
      {hasData ? (
        <Markdown components={{ code: CodeComponent, h3: CustomH3 }}>
          {customFixMarkdown}
        </Markdown>
      ) : (
        <Container>
          <Text size={"md"}>
            {t("searchFindings.tabVuln.contentTab.customFix.criteria.text")}
          </Text>
          <Container
            display={"flex"}
            flexDirection={"column"}
            padding={[1, 1, 1, 1]}
            width={"fit-content"}
          >
            {renderRemediationLanguages()}
          </Container>
        </Container>
      )}
      <div ref={endRef} />
      <CustomFixModal modalRef={modal} />
    </Fragment>
  );
};
